import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '../../../lib/utils';

/**
 * CharlieAccordion — Stacked sections whose bodies stay hidden until their
 * heading is clicked.
 *
 * Built on top of [Radix Accordion](https://www.radix-ui.com/primitives/docs/components/accordion)
 * and styled to match the [ShadCN Accordion](https://ui.shadcn.com/docs/components/accordion).
 *
 * ## Usage
 *
 * ```tsx
 * import {
 *   CharlieAccordion,
 *   CharlieAccordionItem,
 *   CharlieAccordionTrigger,
 *   CharlieAccordionContent,
 * } from '@ver.id/libs.ui.charlie';
 *
 * // Every section collapsed, one open at a time
 * <CharlieAccordion type="single" collapsible>
 *   <CharlieAccordionItem value="purpose">
 *     <CharlieAccordionTrigger>Why your data is needed</CharlieAccordionTrigger>
 *     <CharlieAccordionContent>…</CharlieAccordionContent>
 *   </CharlieAccordionItem>
 * </CharlieAccordion>
 * ```
 *
 * ## Gotchas
 *
 * - `type="single"` closes the open section when another opens; add
 *   `collapsible` so clicking the open heading closes it too. Use
 *   `type="multiple"` to let several sections stay open.
 * - Every item needs a unique `value`. Pass `defaultValue` on the root to
 *   start with a section open.
 * - The trigger renders inside an `h3` (Radix `Header`), so the accordion
 *   brings its own section headings.
 */
export const CharlieAccordion = AccordionPrimitive.Root;

export type CharlieAccordionItemProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Item
>;

export function CharlieAccordionItem({
  className,
  ...props
}: CharlieAccordionItemProps) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

export type CharlieAccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
>;

export function CharlieAccordionTrigger({
  className,
  children,
  ...props
}: CharlieAccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline',
          'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
          'disabled:pointer-events-none disabled:opacity-50',
          '[&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}
        <FontAwesomeIcon
          icon={faChevronDown}
          className="pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

export type CharlieAccordionContentProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Content
>;

export function CharlieAccordionContent({
  className,
  children,
  ...props
}: CharlieAccordionContentProps) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}
